'use client';

import { FC, ReactNode } from 'react';
import { ProductComparisonListStoryblok } from 'storyblok-types';
import { Container } from 'atoms/Container';
import { Image } from 'atoms/Image/Image';
import { Link } from 'atoms/Link/Link';
import { H2, H3 } from 'atoms/Typography';
import { OldPrice } from 'molecules/OldPrice';
import { Price } from 'molecules/Price';
import { useTranslations } from 'modules/i18n/useTranslations';
import { IComparisonProduct, IProductComparisonBlockBody } from './types';
import styles from './styles.module.css';

export const ProductComparisonBlockBody: FC<IProductComparisonBlockBody> = ({
  blok,
  products,
}) => {
  const t = useTranslations();
  const { title, optionsList } = blok;

  const comparisonOptionsData = optionsList.find(
    (option) => option.comparisonOptions,
  );
  const thInlineStyles = {
    backgroundColor: comparisonOptionsData?.background?.color,
    color: comparisonOptionsData?.textColor?.color,
  };

  const renderProductColumn = (
    product: IComparisonProduct,
    value: string | number | ReactNode,
  ) => {
    return (
      <td key={product.referenceKey} className={styles.td}>
        {value}
      </td>
    );
  };

  const renderComparisonRow = (option: ProductComparisonListStoryblok[0]) => {
    return (
      <tr key={option}>
        <th style={thInlineStyles} className={styles.th}>
          {option}
        </th>

        {products?.map((product) => {
          const isOptionSelected = product.options?.some((productOption) =>
            productOption.comparisonOptions?.includes(option),
          );

          return renderProductColumn(
            product,
            isOptionSelected ? (
              <Image
                src={blok.checkmarkIcon.filename}
                alt={blok.checkmarkIcon?.alt}
                className={styles.checkIcon}
              />
            ) : (
              '-'
            ),
          );
        })}
      </tr>
    );
  };

  const renderPriceRow = (
    label: string,
    priceKey: 'old-price' | 'current-price',
  ) => {
    return (
      <tr>
        <th style={thInlineStyles} className={styles.th}>
          {label}
        </th>

        {products?.map((product) => {
          return (
            <td key={product.referenceKey} className={styles.td}>
              {priceKey === 'old-price' ? (
                <OldPrice
                  amount={product.price.recommendedRetailPrice}
                  currencyCode={product.price.currencyCode}
                  withoutLabel
                  className={styles.oldPrice}
                />
              ) : (
                <Price
                  amount={product.price.withTax}
                  currencyCode={product.price.currencyCode}
                  className={styles.price}
                  currencyClassName={styles.currency}
                />
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className={styles.root}>
      <Container>
        {title && <H2 className={styles.title}>{title}</H2>}
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th />
                {products?.map((product) => {
                  return (
                    <th key={product.referenceKey} className={styles.thHead}>
                      {product?.productImage?.filename && (
                        <Image
                          src={product.productImage?.filename}
                          alt={product.productImage?.alt}
                          className={styles.productImage}
                        />
                      )}

                      {product?.productName && (
                        <H3 className={styles.productName}>
                          {product.productName}
                        </H3>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {comparisonOptionsData?.comparisonOptions?.map(
                renderComparisonRow,
              )}
              {renderPriceRow(t('product-comparison.old-price'), 'old-price')}
              {renderPriceRow(t('product-comparison.price'), 'current-price')}
            </tbody>
            <tfoot>
              <tr>
                <th />
                {products?.map((product) => {
                  return (
                    <td key={product.referenceKey} className={styles.tdFoot}>
                      <Link
                        href={product.href}
                        className={styles.link}
                        target="_blank"
                      >
                        {t('product-comparison.link')}
                      </Link>
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </Container>
    </div>
  );
};
